import { Component, computed, inject, signal, output, input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, catchError, of } from 'rxjs';
import { PostTypeService } from '../../../services/post-type.service';
import { PostTypePopulated, PostTypeListResponse } from '../../../models/post-type.model';

/**
 * Reusable dropdown component for selecting post types from an organization
 * 
 * **Features:**
 * - Fetches post types for given organization
 * - Loading and error states
 * - Optional search functionality (when >5 post types)
 * - Single-select mode
 * - Dark mode support
 * - Accessible with ARIA labels
 * - Emits selection changes to parent
 * 
 * **Usage:**
 * ```html
 * <cms-post-type-dropdown
 *   [organizationId]="orgId"
 *   [selectedPostTypeId]="selectedId"
 *   [placeholder]="'Select post type'"
 *   [showSearch]="true"
 *   [searchThreshold]="5"
 *   [disabled]="false"
 *   (selectionChange)="onPostTypeSelected($event)"
 * />
 * ```
 * 
 * @example
 * ```typescript
 * // In parent component
 * selectedPostTypeId = signal<string | null>(null);
 * 
 * onPostTypeSelected(postTypeId: string | null): void {
 *   this.selectedPostTypeId.set(postTypeId);
 *   // Trigger search or other actions
 * }
 * ```
 */
@Component({
  selector: 'cms-post-type-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './post-type-dropdown.component.html',
  styleUrl: './post-type-dropdown.component.css'
})
export class PostTypeDropdownComponent implements OnInit, OnDestroy {
  // Inject services
  private postTypeService = inject(PostTypeService);
  
  // Cleanup subject
  private destroy$ = new Subject<void>();

  // ============================================================================
  // INPUT PROPERTIES
  // ============================================================================

  /**
   * Organization ID (required)
   * Used to fetch post types for the specific organization
   */
  organizationId = input.required<string>();

  /**
   * Currently selected post type ID
   * Pass from parent to show selected value
   */
  selectedPostTypeId = input<string | null>(null);

  /**
   * Placeholder text for dropdown default option
   * @default 'All post types'
   */
  placeholder = input<string>('All post types');

  /**
   * Whether to show search input
   * Search input only appears when post type count > searchThreshold
   * @default true
   */
  showSearch = input<boolean>(true);

  /**
   * Minimum number of post types before search input appears
   * @default 5
   */
  searchThreshold = input<number>(5);

  /**
   * Whether the dropdown is disabled
   * @default false
   */
  disabled = input<boolean>(false);

  // ============================================================================
  // OUTPUT EVENTS
  // ============================================================================

  /**
   * Emitted when user selects or clears a post type
   * Emits object with id and name, or null if cleared
   */
  selectionChange = output<{ id: string; name: string } | null>();

  // ============================================================================
  // INTERNAL STATE (Signals)
  // ============================================================================

  /**
   * List of post types fetched from API
   */
  protected postTypes = signal<PostTypePopulated[]>([]);

  /**
   * Loading state while fetching post types
   */
  protected loading = signal<boolean>(false);

  /**
   * Error message if fetch fails
   */
  protected error = signal<string | null>(null);

  /**
   * Search term for filtering post types
   */
  protected searchTerm = signal<string>('');

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Filtered post types based on search term
   * Searches in typeName
   */
  protected filteredPostTypes = computed(() => {
    const search = this.searchTerm().toLowerCase();
    const allPostTypes = this.postTypes();
    
    if (!search) {
      return allPostTypes;
    }
    
    return allPostTypes.filter(pt => 
      pt.typeName.toLowerCase().includes(search)
    );
  });

  /**
   * Whether to show search input based on threshold
   */
  protected shouldShowSearch = computed(() => {
    return this.showSearch() && this.postTypes().length > this.searchThreshold();
  });

  /**
   * Get selected post type name for display
   */
  protected selectedPostTypeName = computed(() => {
    const selectedId = this.selectedPostTypeId();
    if (!selectedId) return '';
    
    const postType = this.postTypes().find(pt => pt._id === selectedId);
    return postType ? postType.typeName : '';
  });

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    this.fetchPostTypes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /**
   * Fetch post types for the organization
   * 
   * @remarks
   * Called automatically on component init.
   * Can be called manually to refresh the list.
   */
  fetchPostTypes(): void {
    const orgId = this.organizationId();
    
    if (!orgId) {
      console.warn('[PostTypeDropdown] Cannot fetch post types: Organization ID not provided');
      return;
    }
    
    this.loading.set(true);
    this.error.set(null);
    
    this.postTypeService.getPostTypesByOrganization(orgId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('[PostTypeDropdown] Failed to fetch post types:', error);
          this.error.set('Unable to load post types');
          this.loading.set(false);
          return of({ success: false, count: 0, data: [] } as PostTypeListResponse);
        })
      )
      .subscribe((response: PostTypeListResponse) => {
        this.postTypes.set(response.data);
        this.loading.set(false);
        
        console.log(`[PostTypeDropdown] Loaded ${response.data.length} post types`);
      });
  }

  /**
   * Handle post type selection change
   * 
   * @param postTypeId - Selected post type ID or null/empty string to clear
   */
  protected onSelectionChange(postTypeId: string | null): void {
    if (!postTypeId) {
      this.selectionChange.emit(null);
      return;
    }
    
    // Find the post type and emit both id and name
    const postType = this.postTypes().find(pt => pt._id === postTypeId);
    if (postType) {
      this.selectionChange.emit({ id: postType._id, name: postType.typeName });
    } else {
      this.selectionChange.emit(null);
    }
  }

  /**
   * Handle search term change
   * 
   * @param value - Search input value
   */
  protected onSearchChange(value: string): void {
    this.searchTerm.set(value);
  }

  /**
   * Clear search term
   */
  protected clearSearch(): void {
    this.searchTerm.set('');
  }
}
